/**
 * @file: This file makes one list of rows sortable by drag and drop.
 *
 * Three lists use it: the playlists and the items of the scene page, and the
 * sequence of Compose. Each list gives its own row selector and its own drag
 * type, so two lists on one element never react to each other. Both are
 * required: a shared default type would let one drag reach two lists.
 *
 * A row element needs two attributes. `data-drag-id` is the id the caller acts
 * on, and `data-drag-index` is the position of the row, counted from zero. The
 * `dragDropRow` directive writes both.
 *
 * The drop position is a boundary between two rows. `shared/drop-position.js`
 * holds the arithmetic, because the server has to agree on it. This file reads
 * the DOM and reports.
 */

import { moveIndices } from '../../shared/drop-position'

/** Pixels per frame, and the share of the box that starts the auto-scroll. */
const SCROLL_ZONE = Object.freeze({ mobile: 0.2, desktop: 0.15 })
const SCROLL_SPEED = Object.freeze({
  mobile: { min: 5, max: 60 },
  desktop: { min: 2, max: 25 }
})

/** 16 ms is about 60 frames a second, which Android keeps up with. */
const SCROLL_FRAME_MS = 16

/**
 * The boundary the pointer is at.
 *
 * Over the upper half of a row the dragged row lands before it. Over the lower
 * half it lands after it.
 *
 * @param {number} rowIndex - The index of the row under the pointer
 * @param {object} rect - The rectangle of that row, with top and height
 * @param {number} clientY - The vertical position of the pointer
 * @returns {number} The boundary, which is rowIndex or rowIndex + 1
 */
function boundaryAt (rowIndex, rect, clientY) {
  return clientY < rect.top + rect.height / 2 ? rowIndex : rowIndex + 1
}

/** Android and iOS need a wider zone and a higher speed than a mouse does. */
function isTouchPlatform () {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

export default function createDragDropHandler (config) {
  const {
    $scope, $element, $log, onReorder, setDropTarget, rowSelector, dragType,
    scrollContainerSelector = null
  } = config

  if (!rowSelector || !dragType) {
    throw new Error('createDragDropHandler needs a rowSelector and a dragType')
  }

  const state = {
    // The index of the dragged row, and null while no drag of this list runs.
    from: null,
    draggedId: null,
    boundary: null,
    // The scroll box of this drag, looked up once when the drag starts.
    scrollBox: null,
    scrollDirection: 0,
    scrollSpeed: 0,
    clientY: 0,
    scrollInterval: null
  }

  const dragging = () => state.from !== null

  /** The rows of this list, in the order they are on screen. */
  function rows () {
    return Array.from($element[0].querySelectorAll(rowSelector))
  }

  const rowIndexOf = element => parseInt(element.getAttribute('data-drag-index'), 10)

  /** The row under the pointer, when it belongs to this list. */
  function rowUnder (event) {
    const element = event.target && event.target.closest
      ? event.target.closest(rowSelector)
      : null

    return element && $element[0].contains(element) ? element : null
  }

  /** Whether a drag in flight is the one this list started. */
  function isOurs (event) {
    return dragging() &&
      (!event.dataTransfer || Array.from(event.dataTransfer.types || []).includes(dragType))
  }

  /**
   * The boundary the pointer is at.
   *
   * The pointer is often between two rows or under the last one. The nearest
   * boundary is then the answer, so the line follows the pointer everywhere
   * over the list and a drop in the empty space below the list works.
   */
  function boundaryUnder (event) {
    const element = rowUnder(event)

    if (element) {
      return boundaryAt(rowIndexOf(element), element.getBoundingClientRect(), event.clientY)
    }

    return nearestBoundary(event.clientY)
  }

  function nearestBoundary (clientY) {
    const all = rows()

    if (all.length === 0) {
      return null
    }

    const first = all[0].getBoundingClientRect()

    if (clientY < first.top) {
      return 0
    }

    const last = all[all.length - 1].getBoundingClientRect()

    return clientY > last.top + last.height ? all.length : null
  }

  /**
   * Tell the caller where the row would land. The two boundaries beside the row
   * itself are no move, so no line is drawn for them: a line that promises
   * nothing is worse than no line.
   */
  function report (boundary) {
    const shown = boundary !== null && moveIndices(state.from, boundary) ? boundary : null

    if (shown === state.boundary) {
      return
    }

    state.boundary = shown

    if (setDropTarget) {
      setDropTarget(shown === null ? { index: null, from: null } : { index: shown, from: state.from })
    }

    render()
  }

  /**
   * Draw at the end of the digest that is running, or start one. The pointer
   * moves faster than a timer, so a throttle makes the line lag behind it.
   */
  function render () {
    if ($scope.$root && $scope.$root.$$phase) {
      $scope.$evalAsync()
      return
    }

    $scope.$applyAsync()
  }

  // ===== Auto-scroll =====

  /**
   * The box that scrolls while a row is dragged, or null when the list did not
   * ask for the auto-scroll or the box cannot scroll.
   */
  function findScrollBox () {
    if (!scrollContainerSelector) {
      return null
    }

    const box = $element[0].querySelector(scrollContainerSelector)

    return box && box.scrollHeight > box.clientHeight ? box : null
  }

  function measureScroll (clientY) {
    const box = state.scrollBox
    const rect = box.getBoundingClientRect()
    const touch = isTouchPlatform()
    const zone = rect.height * (touch ? SCROLL_ZONE.mobile : SCROLL_ZONE.desktop)
    const speed = touch ? SCROLL_SPEED.mobile : SCROLL_SPEED.desktop
    const fromTop = clientY - rect.top
    const fromBottom = rect.bottom - clientY
    const canScrollUp = box.scrollTop > 0
    const canScrollDown = box.scrollTop < box.scrollHeight - box.clientHeight

    state.scrollDirection = 0
    state.scrollSpeed = 0

    // Outside the box the speed is the highest one, so the list keeps moving
    // while the pointer waits above or below it.
    const share = direction => {
      const distance = direction < 0 ? fromTop : fromBottom
      return distance < 0 ? 1 : (zone - distance) / zone
    }

    const direction = (fromTop < zone && canScrollUp)
      ? -1
      : ((fromBottom < zone && canScrollDown) ? 1 : 0)

    if (direction === 0) {
      return
    }

    const part = Math.max(0, Math.min(1, share(direction)))
    state.scrollDirection = direction
    state.scrollSpeed = speed.min + part * (speed.max - speed.min)
  }

  function scrollFrame () {
    if (!state.scrollBox) {
      return
    }

    measureScroll(state.clientY)

    if (state.scrollDirection !== 0) {
      state.scrollBox.scrollTop += state.scrollDirection * state.scrollSpeed
    }
  }

  function startScrolling () {
    state.scrollBox = findScrollBox()

    if (!state.scrollBox) {
      return
    }

    stopScrolling()
    state.scrollInterval = setInterval(scrollFrame, SCROLL_FRAME_MS)
  }

  function stopScrolling () {
    if (state.scrollInterval) {
      clearInterval(state.scrollInterval)
      state.scrollInterval = null
    }

    state.scrollDirection = 0
  }

  // ===== The four events of a drag =====

  function handleDragStart (event) {
    const element = rowUnder(event)
    const id = element && element.getAttribute('data-drag-id')

    if (!element || !id) {
      return
    }

    state.from = rowIndexOf(element)
    state.draggedId = id
    state.boundary = null
    state.clientY = event.clientY
    event.dataTransfer.setData(dragType, id)
    event.dataTransfer.effectAllowed = 'move'
    startScrolling()
    // No line yet. The row has not moved, so there is nothing to promise.
    render()
  }

  /**
   * A drag over the list is always prevented, whatever it holds.
   *
   * Without this the browser opens a file or follows a link that is dropped on
   * the page, and an unsaved Compose session goes away with it.
   */
  function handleDragOver (event) {
    event.preventDefault()

    if (!isOurs(event)) {
      return
    }

    state.clientY = event.clientY
    report(boundaryUnder(event))
  }

  function handleDrop (event) {
    event.preventDefault()

    if (!isOurs(event)) {
      return
    }

    const move = moveFor(boundaryUnder(event))

    finish()

    if (move && onReorder) {
      onReorder(move)
    }

    $scope.$apply()
  }

  /**
   * The move a drop asks for, or null.
   *
   * The source is found again by its id. A live update can draw the list again
   * in the middle of a drag, and the index of the dragstart is then stale. A
   * row that is gone is no move at all.
   */
  function moveFor (boundary) {
    if (boundary === null) {
      return null
    }

    const all = rows()
    const from = all.findIndex(element => element.getAttribute('data-drag-id') === state.draggedId)

    if (from < 0) {
      $log.debug('[DragDrop] The dragged row is gone, so nothing moves')
      return null
    }

    const indices = moveIndices(from, boundary)
    const target = indices && all[indices.targetIndex]
    const targetId = target && target.getAttribute('data-drag-id')

    if (!targetId || targetId === state.draggedId) {
      return null
    }

    return { draggedId: state.draggedId, targetId, from, to: indices.to, side: indices.side }
  }

  function finish () {
    report(null)
    state.from = null
    state.draggedId = null
    state.boundary = null
    state.scrollBox = null
    stopScrolling()
  }

  function handleDragEnd () {
    if (!dragging()) {
      return
    }

    finish()
    $scope.$apply()
  }

  /** The pointer left the list. Nothing lands here, so nothing is marked. */
  function handleDragLeave (event) {
    if (!dragging() || (event.relatedTarget && $element[0].contains(event.relatedTarget))) {
      return
    }

    report(null)
  }

  return {
    initialize () {
      $element[0].addEventListener('dragstart', handleDragStart)
      $element[0].addEventListener('dragover', handleDragOver)
      $element[0].addEventListener('drop', handleDrop)
      $element[0].addEventListener('dragend', handleDragEnd)
      $element[0].addEventListener('dragleave', handleDragLeave)
    },

    destroy () {
      stopScrolling()
      $element[0].removeEventListener('dragstart', handleDragStart)
      $element[0].removeEventListener('dragover', handleDragOver)
      $element[0].removeEventListener('drop', handleDrop)
      $element[0].removeEventListener('dragend', handleDragEnd)
      $element[0].removeEventListener('dragleave', handleDragLeave)
    }
  }
}

// `moveIndices` comes from shared/drop-position.js, because the server reads
// the same rule. It is passed on here so a caller has one place to import from.
export { boundaryAt, moveIndices }
